import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import * as swimmingService from '../services/swimmingService';
import {
  Trophy, User, Building2, Medal
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   SwimmingCompetitionResultsPage — Competition Results
   Route: /natacao/resultados
   
   Uses the same header + filter style as SwimmingPage (/natacao):
   - PageHeader with year selector
   - Horizontal scroll competition pills with logos
   - Labeled dropdown filters (Gender, Type, Event, Phase)
   - Results table with medals, diff, RT
   ═══════════════════════════════════════════════════════════════ */

// ── Helpers ──────────────────────────────────────────────────

const formatEventName = (distance, stroke, gender, isRelay) => {
  const strokeMap = {
    'Free': 'Livre', 'Freestyle': 'Livre', 'Backstroke': 'Costas', 'Back': 'Costas',
    'Breaststroke': 'Peito', 'Breast': 'Peito', 'Butterfly': 'Borboleta', 'Fly': 'Borboleta',
    'Medley': 'Medley', 'IM': 'Medley', 'NADO LIVRE': 'Livre', 'LIVRE': 'Livre',
    'COSTAS': 'Costas', 'PEITO': 'Peito', 'BORBOLETA': 'Borboleta', 'MEDLEY': 'Medley',
    'Livre': 'Livre', 'Costas': 'Costas', 'Peito': 'Peito', 'Borboleta': 'Borboleta',
  };
  const strokePt = strokeMap[stroke] || stroke;
  const relayText = isRelay ? ' Rev.' : '';
  return `${distance}m ${strokePt}${relayText}`;
};

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const min = Math.floor(seconds / 60);
  const sec = (seconds % 60).toFixed(2);
  return min > 0 ? `${min}:${sec.padStart(5, '0')}` : sec;
};

const formatDiff = (diffSeconds) => {
  if (diffSeconds === null || diffSeconds === undefined || diffSeconds === 0) return '';
  const sign = diffSeconds > 0 ? '+' : '';
  const min = Math.floor(Math.abs(diffSeconds) / 60);
  const sec = (Math.abs(diffSeconds) % 60).toFixed(2);
  if (min > 0) return `${sign}${min}:${sec.padStart(5, '0')}`;
  return `${sign}${diffSeconds.toFixed(2)}`;
};

const COUNTRY_FLAGS = {
  BRA: '🇧🇷', USA: '🇺🇸', AUS: '🇦🇺', GBR: '🇬🇧', CHN: '🇨🇳', JPN: '🇯🇵',
  ITA: '🇮🇹', GER: '🇩🇪', FRA: '🇫🇷', HUN: '🇭🇺', CAN: '🇨🇦', NED: '🇳🇱',
  SWE: '🇸🇪', RSA: '🇿🇦', KOR: '🇰🇷', ESP: '🇪🇸', ROU: '🇷🇴', NZL: '🇳🇿',
  CRO: '🇭🇷', SRB: '🇷🇸', POL: '🇵🇱', CZE: '🇨🇿', SUI: '🇨🇭', IRL: '🇮🇪',
  ISR: '🇮🇱', TUR: '🇹🇷',
};

// ── Competition logos & display names (same as SwimmingPage) ──

const getCompetitionLogo = (name) => {
  if (!name) return null;
  const logos = {
    'TROFÉU JOSÉ FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFEU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    'TROFEU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    'PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS DE PARIS': '/logo campeonatos natacao/paris2024.png',
    'OLIMPIADAS DE PARIS': '/logo campeonatos natacao/paris2024.png',
    'JOGOS OLÍMPICOS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'JOGOS OLIMPICOS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'JUNIOR PAN PACIFIC CHAMPIONSHIPS': '/logo campeonatos natacao/juniorpanpacs.png',
    'AUSTRALIAN SWIMMING TRIALS': '/logo campeonatos natacao/austrao trials.png',
    'TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'USA SWIMMING NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US OPEN': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'TOYOTA US OPEN': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'WORLD AQUATICS CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'WORLD CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'DOHA 2024': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'SINGAPORE 2025': '/logo campeonatos natacao/wolidchampionshipssingapure2025.png',
    'CBDA': '/logo campeonatos natacao/cbda.png',
  };
  const norm = name.toUpperCase().trim();
  if (logos[norm]) return logos[norm];
  for (const [key, val] of Object.entries(logos)) {
    if (norm.includes(key)) return val;
  }
  return null;
};

const getCompetitionDisplayName = (fullName) => {
  if (!fullName) return fullName;
  const names = {
    'OLIMPIADAS DE PARIS': 'Olimpíadas', 'WORLD AQUATICS CHAMPIONSHIPS': 'Mundial',
    'WORLD AQUATICS JUNIOR SWIMMING CHAMPIONSHIPS': 'Mundial Junior',
    'AUSTRALIAN SWIMMING TRIALS': 'Australian Trials',
    'TOYOTA NATIONAL CHAMPIONSHIPS': 'Toyota Nationals',
    'PHILLIPS 66 NATIONAL CHAMPIONSHIPS': 'Phillips 66 Nationals',
    'TROFEU MARIA LENK': 'Maria Lenk', 'TROFEU JOSE FINKEL': 'José Finkel',
    'CAMPEONATO BRASILEIRO DE SELEÇÕES': 'Brasileiro de Seleções',
    'JUNIOR PAN PACIFIC CHAMPIONSHIPS': 'Jr Pan Pacific',
  };
  const norm = fullName.toUpperCase().trim();
  if (names[norm]) return names[norm];
  for (const [key, val] of Object.entries(names)) {
    if (norm.includes(key)) return val;
  }
  return fullName.replace(/\s*\d{4}\s*$/g, '').trim();
};

const getCompetitionVenue = (fullName) => {
  if (!fullName) return '';
  const venues = {
    'OLIMPIADAS DE PARIS': 'Paris', 'WORLD AQUATICS CHAMPIONSHIPS': 'Singapura',
    'AUSTRALIAN SWIMMING TRIALS': 'Brisbane',
    'TOYOTA NATIONAL CHAMPIONSHIPS': 'Indianapolis',
    'PHILLIPS 66 NATIONAL CHAMPIONSHIPS': 'Indianapolis',
  };
  const norm = fullName.toUpperCase().trim();
  if (venues[norm]) return venues[norm];
  for (const [key, val] of Object.entries(venues)) {
    if (norm.includes(key)) return val;
  }
  return '';
};

// Phase ordering for sorting
const PHASE_ORDER = {
  'Final': 1, 'Final A': 1, 'Final B': 2, 'Final C': 3, 'Final D': 4,
  'Semifinal': 5, 'Semifinals': 5,
  'Eliminatórias': 6, 'Eliminatória': 6, 'Eliminatorias': 6, 'Heat': 6, 'Heats': 6,
};

const PHASE_LABELS = {
  'Final': 'Final', 'Final A': 'Final A', 'Final B': 'Final B',
  'Final C': 'Final C', 'Final D': 'Final D',
  'Semifinal': 'Semifinal', 'Semifinals': 'Semifinal',
  'Eliminatórias': 'Eliminatórias', 'Eliminatória': 'Eliminatórias',
  'Eliminatorias': 'Eliminatórias', 'Heat': 'Eliminatórias', 'Heats': 'Eliminatórias',
};

// ── Results Table ────────────────────────────────────────────

const ResultsTable = ({ results, loading }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="h-12 rounded-xl animate-pulse bg-green-50" />
        ))}
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-green-50">
          <Trophy size={24} className="text-gray-400" />
        </div>
        <p className="text-sm font-bold text-gray-700">Selecione um campeonato e uma prova</p>
        <p className="text-xs mt-1 text-gray-400">Use os filtros acima para visualizar os resultados</p>
      </div>
    );
  }

  const firstTime = results[0]?.time_seconds;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[700px]">
        <thead>
          <tr className="border-b border-gray-200">
            {['#', 'Atleta', 'Clube', 'Tempo', 'Dif. 1º', 'RT'].map((h) => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.map((r, idx) => {
            const pos = r.position || idx + 1;
            const isMedal = pos <= 3;
            const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
            const diff = (firstTime && r.time_seconds) ? r.time_seconds - firstTime : null;

            return (
              <tr key={`${r.swimmer_id || r.athlete_name}-${idx}`}
                className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3 w-12">
                  {isMedal ? (
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${medalColors[pos - 1]}20`, color: medalColors[pos - 1] }}>
                      <Medal size={14} />
                    </div>
                  ) : (
                    <span className="text-xs font-extrabold text-gray-400">{pos}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.swimmer_id ? (
                    <Link to={`/natacao/nadador/${r.swimmer_id}`} className="flex items-center gap-2 group">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50">
                        <User size={14} style={{ color: BRAND_GREEN }} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-800 group-hover:underline">
                          {r.swimmer_name || r.athlete_name}
                        </p>
                        {r.nationality && (
                          <p className="text-[10px] text-gray-400">
                            {COUNTRY_FLAGS[r.nationality] || ''} {r.nationality}
                          </p>
                        )}
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50">
                        <User size={14} style={{ color: BRAND_GREEN }} />
                      </div>
                      <p className="text-xs font-bold text-gray-800">{r.swimmer_name || r.athlete_name || '—'}</p>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Building2 size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-700">{r.club_name || '—'}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-extrabold font-mono" style={{ color: BRAND_GREEN }}>
                    {r.time_formatted || formatTime(r.time_seconds) || '—'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {pos === 1 ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-green-50"
                      style={{ color: BRAND_GREEN }}>REF</span>
                  ) : diff !== null ? (
                    <span className="text-xs font-mono font-bold text-gray-400">{formatDiff(diff)}</span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono text-gray-400">
                    {r.reaction_time ? r.reaction_time.toFixed(2) : '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────

const SwimmingCompetitionResultsPage = () => {
  // Data
  const [competitions, setCompetitions] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [eventsWithPhases, setEventsWithPhases] = useState([]);

  // Loading
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);
  const [loading, setLoading] = useState(false);

  // Selections
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedGender, setSelectedGender] = useState('Feminino');
  const [selectedRelayType, setSelectedRelayType] = useState('individual');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRound, setSelectedRound] = useState('Final');
  const [selectedEventPhases, setSelectedEventPhases] = useState([]);

  // ── Load competitions ──
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCompetitions(true);
        const response = await swimmingService.getCompetitions();
        const data = Array.isArray(response) ? response : (response.competitions || []);
        setCompetitions(data);

        const years = [...new Set(data.map(c => c.year))].sort((a, b) => b - a);
        if (years.length > 0) {
          setAvailableYears(years);
          if (!years.includes(selectedYear)) setSelectedYear(years[0]);
        }
      } catch (err) {
        console.error('[Results] Competitions error:', err);
      } finally {
        setLoadingCompetitions(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Competitions filtered by year
  const availableCompetitions = useMemo(() => {
    return competitions
      .filter(c => c.year === selectedYear)
      .map(c => ({ id: c.id, name: c.name, year: c.year, country: c.country }));
  }, [competitions, selectedYear]);

  // Auto-select default competition when year changes
  useEffect(() => {
    if (availableCompetitions.length > 0) {
      const ml = availableCompetitions.find(c => c.name.toLowerCase().includes('maria lenk'));
      const mundial = availableCompetitions.find(c =>
        c.name.toLowerCase().includes('world') || c.name.toLowerCase().includes('mundial'));
      const def = ml || mundial || availableCompetitions[0];
      setSelectedCompetition(def);
      setSelectedCompetitionId(def.id);
    } else {
      setSelectedCompetition(null);
      setSelectedCompetitionId(null);
    }
  }, [availableCompetitions]);

  // ── Load events with phases when competition changes ──
  useEffect(() => {
    const loadData = async () => {
      if (!selectedCompetitionId) {
        setEventsWithPhases([]);
        setEvents([]);
        setSelectedEvent(null);
        return;
      }

      try {
        // Load all results to extract phases per event (same approach as SwimmingPage)
        const resultsResponse = await swimmingService.getResults({
          competition_id: selectedCompetitionId,
          limit: 10000,
        });
        const allResults = Array.isArray(resultsResponse)
          ? resultsResponse
          : (resultsResponse.results || []);

        // Group by event → phases
        const eventPhasesMap = {};
        allResults.forEach(r => {
          const eid = r.event_id;
          const phase = r.fase || r.phase;
          if (eid && phase) {
            if (!eventPhasesMap[eid]) {
              eventPhasesMap[eid] = {
                event_id: eid, event_name: r.event_name, phases: new Set(),
              };
            }
            eventPhasesMap[eid].phases.add(phase);
          }
        });

        const eventsData = Object.values(eventPhasesMap).map(e => ({
          ...e,
          phases: Array.from(e.phases).sort((a, b) =>
            (PHASE_ORDER[a] || 999) - (PHASE_ORDER[b] || 999)),
        }));
        setEventsWithPhases(eventsData);
      } catch (err) {
        console.error('[Results] Events-phases error:', err);
        setEventsWithPhases([]);
      }
    };
    loadData();
  }, [selectedCompetitionId]);

  // ── Load events filtered by gender + relay ──
  useEffect(() => {
    const loadEvents = async () => {
      if (!selectedCompetitionId) {
        setEvents([]);
        setSelectedEvent(null);
        return;
      }

      try {
        setSelectedEvent(null);
        const params = { competition_id: selectedCompetitionId };
        if (selectedGender) {
          params.gender = selectedGender === 'Masculino' ? 'M' : 'F';
        }
        if (selectedRelayType) {
          params.is_relay = selectedRelayType === 'relay' ? 1 : 0;
        }

        const response = await swimmingService.getEvents(params);
        const data = Array.isArray(response) ? response : (response.events || []);
        setEvents(data);

        if (data.length > 0) {
          setSelectedEvent(data[0].id || data[0].event_id);
        }
      } catch (err) {
        console.error('[Results] Events error:', err);
        setEvents([]);
      }
    };
    loadEvents();
  }, [selectedCompetitionId, selectedGender, selectedRelayType]);

  // ── Update available phases when event changes ──
  useEffect(() => {
    if (!selectedEvent || eventsWithPhases.length === 0) {
      setSelectedEventPhases([]);
      return;
    }
    const current = eventsWithPhases.find(e => (e.event_id || e.id) === selectedEvent);
    if (current?.phases?.length > 0) {
      setSelectedEventPhases(current.phases);
      if (!current.phases.includes(selectedRound)) {
        setSelectedRound(current.phases[0]);
      }
    } else {
      setSelectedEventPhases([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, eventsWithPhases]);

  // ── Load results ──
  useEffect(() => {
    const loadResults = async () => {
      if (!selectedCompetitionId || !selectedEvent) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await swimmingService.getResults({
          competition_id: selectedCompetitionId,
          event_id: selectedEvent,
          fase: selectedRound,
        });
        const data = Array.isArray(response) ? response : (response.results || []);
        setResults(data);
      } catch (err) {
        console.error('[Results] Results error:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [selectedCompetitionId, selectedEvent, selectedRound]);

  // ── Loading state ──
  if (loadingCompetitions && competitions.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4"
            style={{ borderColor: BRAND_GREEN }} />
          <p className="text-lg font-semibold" style={{ color: BRAND_GREEN }}>Carregando competições...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 lg:px-6 space-y-6 w-full">

      {/* ═══ HEADER ═══ */}
      <PageHeader
        title="Natação — Resultados"
        subtitle={selectedCompetition
          ? `${selectedCompetition.name} ${selectedCompetition.year || ''}`
          : 'Selecione uma competição para ver os resultados'}
        badgeText="COMPETIÇÕES"
        badgeIcon={true}
        rightSlot={
          <div className="min-w-[120px]">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">Ano</span>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
              style={{ color: BRAND_GREEN, focusRingColor: BRAND_GREEN }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />

      {/* ═══ RESULTS SECTION ═══ */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-sm p-4 lg:p-5 w-full min-w-0">

        {/* Section title + year */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full" style={{ backgroundColor: BRAND_GOLD }} />
            <div className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: BRAND_GOLD, color: 'white' }}>
              <Trophy size={14} />
            </div>
            <h2 className="text-xl lg:text-2xl font-bold" style={{ color: BRAND_GREEN }}>
              Resultados da Prova
            </h2>
          </div>
          <div className="min-w-[120px]">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
              style={{ color: BRAND_GREEN }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="space-y-4 mb-4">

          {/* Horizontal competition scroll */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: BRAND_GREEN }}>Competição</label>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {availableCompetitions.map(comp => (
                <button
                  key={comp.id}
                  onClick={() => { setSelectedCompetitionId(comp.id); setSelectedCompetition(comp); }}
                  className={`flex-shrink-0 px-5 py-4 rounded-lg border-2 transition-all duration-200 ${
                    selectedCompetitionId === comp.id
                      ? 'bg-gradient-to-br from-green-50 to-white shadow-md'
                      : 'bg-white hover:border-gray-400 hover:shadow-sm'
                  }`}
                  style={{
                    minWidth: '220px',
                    borderColor: selectedCompetitionId === comp.id ? BRAND_GREEN : '#d1d5db',
                  }}
                >
                  <div className="flex items-center gap-3">
                    {getCompetitionLogo(comp.name) ? (
                      <img
                        src={getCompetitionLogo(comp.name)}
                        alt={comp.name}
                        className="w-10 h-10 object-contain rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: BRAND_GREEN }}>
                        <Trophy size={18} color="white" />
                      </div>
                    )}
                    <div className="text-left">
                      <div className="font-bold text-base" style={{ color: BRAND_GREEN }}>
                        {getCompetitionDisplayName(comp.name)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getCompetitionVenue(comp.name) || comp.year}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Dropdowns: Gender, Type, Event, Phase */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">Gênero</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
                style={{ color: BRAND_GREEN }}
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
              >
                <option value="Feminino">Feminino</option>
                <option value="Masculino">Masculino</option>
              </select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">Tipo</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
                style={{ color: BRAND_GREEN }}
                value={selectedRelayType}
                onChange={(e) => setSelectedRelayType(e.target.value)}
              >
                <option value="individual">Individual</option>
                <option value="relay">Revezamento</option>
              </select>
            </div>

            <div className="flex-1 min-w-[240px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">Prova</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
                style={{ color: BRAND_GREEN }}
                value={selectedEvent || ''}
                onChange={(e) => setSelectedEvent(Number(e.target.value))}
              >
                {events.length === 0 ? (
                  <option>Nenhuma prova disponível</option>
                ) : (
                  events.map(event => {
                    const eid = event.event_id || event.id;
                    const ename = event.event_name || event.name || formatEventName(
                      event.distance_meters || event.distance,
                      event.stroke,
                      event.gender,
                      event.relay || event.is_relay
                    );
                    return <option key={eid} value={eid}>{ename}</option>;
                  })
                )}
              </select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-gray-600 mb-1">Fase</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:ring-2 focus:outline-none"
                style={{ color: BRAND_GREEN }}
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
              >
                {selectedEventPhases.length === 0 ? (
                  <option>Nenhuma fase disponível</option>
                ) : (
                  selectedEventPhases.map(p => (
                    <option key={p} value={p}>{PHASE_LABELS[p] || p}</option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        {/* ── Results Table ── */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-4 mx-auto mb-3"
                style={{ borderColor: BRAND_GREEN }} />
              <p className="text-sm text-gray-500">Carregando resultados...</p>
            </div>
          </div>
        ) : (
          <>
            {results.length > 0 && (
              <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
                <span className="font-bold" style={{ color: BRAND_GREEN }}>{results.length}</span>
                <span>atletas</span>
                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-green-50"
                  style={{ color: BRAND_GREEN }}>
                  {PHASE_LABELS[selectedRound] || selectedRound}
                </span>
              </div>
            )}
            <ResultsTable results={results} loading={false} />
          </>
        )}
      </div>
    </div>
  );
};

export default SwimmingCompetitionResultsPage;
